import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, Clock3, Download, FileText, RefreshCw, Search, Wallet } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import ReportPeriodPicker, { CalendarReportPeriod, getReportPeriodRange, moveReportPeriod, ReportPeriod } from "./ReportPeriodPicker.tsx";
import "./CSS/DetailsReport.css";

const API_BASE = "https://timetrakerapi.sntservices.in";
const PAGE_SIZE = 50;
interface StaffOption { email: string; name?: string | null; }
interface ProjectOption { id: number; name?: string | null; }
interface ReportRow { id: number; date: string; empname?: string | null; projectName?: string | null; note?: string | null; totaltime: string; money: number; }
const csvCell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const secondsOf = (time?: string) => {
  const match = time?.match(/^(\d+):(\d{1,2})(?::(\d{1,2}))?/);
  return match ? Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] || 0) : 0;
};
const displaySeconds = (value: number) => `${Math.floor(value / 3600)}h ${Math.floor(value % 3600 / 60)}m`;
const currency = (value: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);
const initialRange = getReportPeriodRange("month", new Date());

export default function DetailsReport() {
  const { user } = useUser();
  const adminId = user?.id;
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [email, setEmail] = useState("");
  const [projectId, setProjectId] = useState("");
  const [period, setPeriod] = useState<ReportPeriod>("month");
  const [anchorDate, setAnchorDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(initialRange.start);
  const [endDate, setEndDate] = useState<Date>(initialRange.end);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [appliedLabel, setAppliedLabel] = useState("");

  useEffect(() => {
    if (!adminId) return;
    const controller = new AbortController();
    Promise.all([
      axios.get<StaffOption[]>(`${API_BASE}/GetUser`, { params: { Adminid: adminId }, signal: controller.signal }),
      axios.get<ProjectOption[]>(`${API_BASE}/GetProject`, { params: { Adminid: adminId }, signal: controller.signal }),
    ]).then(([people, availableProjects]) => {
      setStaff(Array.isArray(people.data) ? people.data : []);
      setProjects(Array.isArray(availableProjects.data) ? availableProjects.data : []);
    }).catch((err) => { if (!axios.isCancel(err)) setError("Some filters could not be loaded."); });
    return () => controller.abort();
  }, [adminId]);

  const fetchReport = useCallback(async (from: Date, to: Date, selectedEmail: string, selectedProject: string, signal?: AbortSignal) => {
    if (!adminId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const response = await axios.get<ReportRow[]>(`${API_BASE}/GetDetailsReport`, { params: {
        Adminid: adminId, SelectedtoDate: format(from, "yyyy-MM-dd"), SelectedfromDate: format(to, "yyyy-MM-dd"),
        projectId: selectedProject || 0, ...(selectedEmail ? { email: selectedEmail } : {}),
      }, signal });
      if (signal?.aborted) return;
      setRows(Array.isArray(response.data) ? response.data : []);
      setPage(1);
      setAppliedLabel(`${format(from, "MMM d, yyyy")} – ${format(to, "MMM d, yyyy")}`);
    } catch (err) {
      if (signal?.aborted || axios.isCancel(err)) return;
      setError("Could not load the detailed report. Please try again.");
    } finally { if (!signal?.aborted) setLoading(false); }
  }, [adminId]);

  useEffect(() => {
    const controller = new AbortController();
    fetchReport(startDate, endDate, "", "", controller.signal);
    return () => controller.abort();
    // Initial current-month report; later preset changes run from their controls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReport]);

  const searchReport = (event: React.FormEvent) => {
    event.preventDefault();
    if (startDate > endDate) { setError("Start date must be on or before end date."); return; }
    fetchReport(startDate, endDate, email, projectId);
  };
  const selectPeriod = (nextPeriod: CalendarReportPeriod, nextAnchor = new Date()) => {
    const range = getReportPeriodRange(nextPeriod, nextAnchor);
    setPeriod(nextPeriod);
    setAnchorDate(nextAnchor);
    setStartDate(range.start);
    setEndDate(range.end);
    fetchReport(range.start, range.end, email, projectId);
  };
  const navigatePeriod = (direction: -1 | 1) => {
    if (period === "custom" || period === "all") return;
    selectPeriod(period, moveReportPeriod(period, anchorDate, direction));
  };
  const fetchAll = async () => {
    if (!adminId) return;
    setPeriod("all");
    setLoading(true);
    setError("");
    try {
      const response = await axios.get<ReportRow[] | string>(`${API_BASE}/GetDetailsReportAllData`, { params: { Adminid: adminId } });
      setRows(Array.isArray(response.data) ? response.data : []);
      setPage(1);
      setAppliedLabel("All time · all team members · all projects");
    } catch { setError("Could not load all-time records. Try a shorter date range."); }
    finally { setLoading(false); }
  };

  const totals = useMemo(() => ({
    seconds: rows.reduce((sum, row) => sum + secondsOf(row.totaltime), 0),
    money: rows.reduce((sum, row) => sum + (Number(row.money) || 0), 0),
  }), [rows]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const exportCsv = () => {
    if (!rows.length) return;
    const data = [["ID", "Date", "Employee", "Project", "Note", "Total time", "Money"], ...rows.map((row) => [row.id, row.date, row.empname, row.projectName, row.note, row.totaltime, row.money])];
    const csv = data.map((record) => record.map(csvCell).join(",")).join("\r\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a"); link.href = url; link.download = "Detail_Report.csv"; link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const exportPdf = async () => {
    if (!rows.length) return;
    setExporting(true);
    try {
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
      const doc = new jsPDF({ orientation: "landscape" });
      autoTable(doc, { head: [["ID", "Date", "Employee", "Project", "Note", "Total time", "Money"]], body: rows.map((row) => [row.id, row.date, row.empname || "", row.projectName || "", row.note || "", row.totaltime, row.money]) });
      doc.save("Detail_Report.pdf");
    } catch { setError("Could not create the PDF. Please try again."); }
    finally { setExporting(false); }
  };

  return <main className="details-report-page">
    <header className="details-report-header"><div><span>REPORTS / TIME ENTRIES</span><h1>Detail report</h1><p>Review individual time entries and export the current results.</p></div><div className="details-export"><button type="button" onClick={exportCsv} disabled={!rows.length || loading}><Download size={16} /> CSV</button><button type="button" onClick={exportPdf} disabled={!rows.length || loading || exporting}><FileText size={16} /> {exporting ? "Creating…" : "PDF"}</button></div></header>
    <form className="details-report-filters" onSubmit={searchReport}>
      <label><span>Team member</span><select value={email} onChange={(event) => setEmail(event.target.value)}><option value="">All team members</option>{staff.map((person) => <option key={person.email} value={person.email}>{person.name || "Name unavailable"} — {person.email}</option>)}</select></label>
      <label><span>Project</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">All projects</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name || "Unnamed project"}</option>)}</select></label>
      <button type="submit" disabled={loading || period === "all"}><Search size={16} /> {loading ? "Loading…" : "Apply filters"}</button>
      {period === "all" && <small className="details-all-hint"><RefreshCw size={13} /> Choose a time span to use team or project filters.</small>}
      <ReportPeriodPicker period={period} anchorDate={anchorDate} startDate={startDate} endDate={endDate} loading={loading} appliedRange={appliedLabel} allowAllTime showCustomApply={false} onPeriodChange={selectPeriod} onNavigate={navigatePeriod} onCurrentPeriod={() => selectPeriod(period as CalendarReportPeriod)} onCustomMode={() => setPeriod("custom")} onAllTime={fetchAll} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
    </form>
    {error && <div className="details-report-error" role="alert">{error}</div>}
    <section className="details-report-metrics" aria-label="Report totals"><div><FileText size={19} /><span>Entries</span><strong>{loading ? "—" : rows.length}</strong></div><div><Clock3 size={19} /><span>Logged time</span><strong>{loading ? "—" : displaySeconds(totals.seconds)}</strong></div><div><Wallet size={19} /><span>Amount</span><strong>{loading ? "—" : currency(totals.money)}</strong></div></section>
    <section className="details-report-panel"><div className="details-report-panel-header"><div><h2>Time entries</h2><p>{appliedLabel || "Loading recent entries…"}</p></div><span>{rows.length} records</span></div>
      <div className="details-report-table-wrap"><table><thead><tr><th>ID</th><th>Date</th><th>Employee</th><th>Project</th><th>Note</th><th>Time</th><th>Amount</th></tr></thead><tbody>{loading ? <tr><td colSpan={7} className="details-report-empty">Loading entries…</td></tr> : pageRows.length ? pageRows.map((row) => <tr key={row.id}><td>{row.id}</td><td>{row.date}</td><td>{row.empname || "—"}</td><td>{row.projectName || "—"}</td><td className="details-report-note" title={row.note || ""}>{row.note || "—"}</td><td>{row.totaltime}</td><td>{currency(row.money)}</td></tr>) : <tr><td colSpan={7} className="details-report-empty">No entries found for these filters.</td></tr>}</tbody></table></div>
      <div className="details-report-footer"><span>Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}</span><div><button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page <= 1}><ChevronLeft size={16} /> Previous</button><span>Page {page} of {pageCount}</span><button type="button" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={page >= pageCount}>Next <ChevronRight size={16} /></button></div></div>
    </section>
  </main>;
}
